import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { Project } from '../models/types';
import '../styles/Settings.css';
import { getSyncStatus, getPendingCount, testConnection, syncTimeEntries } from '../services/syncService';
import FeedbackForm from './FeedbackForm';
import EnrollmentModal from './EnrollmentModal';

// Default URL for syncing projects - this can be updated in the app settings
const DEFAULT_SYNC_URL = localStorage.getItem('projectsSyncUrl') || 'ProjectTaskList.json';

interface SettingsProps {
  onClose: () => void;
  onSave: () => void;
}

type Tab = 'projects' | 'preferences' | 'sync' | 'feedback';

const Settings: React.FC<SettingsProps> = ({ onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState<Tab>('projects');
  
  // Projects State
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectLocation, setNewProjectLocation] = useState(localStorage.getItem('defaultLocation') || 'Site');
  const [newTaskName, setNewTaskName] = useState('');
  const [importData, setImportData] = useState('');
  const [importError, setImportError] = useState('');
  const [syncUrl, setSyncUrl] = useState(DEFAULT_SYNC_URL);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState('');

  // Preferences State
  const [showLocation, setShowLocation] = useState(localStorage.getItem('showLocationButtons') !== 'false');
  const [enableOvernight, setEnableOvernight] = useState(localStorage.getItem('enableOvernightShifts') === 'true');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');

  // Sync State
  const [status, setStatus] = useState(getSyncStatus());
  // Counted from the database rather than tracked in memory. The old
  // `syncStatus.pendingEntries` was only ever assigned 0, so this panel
  // reported "nothing waiting" while work sat on the device.
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Device enrollment State
  const [enrolledCode, setEnrolledCode] = useState<string | null>(null);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [changeCodeError, setChangeCodeError] = useState<string | null>(null);

  useEffect(() => {
    db.getEnrollmentCode().then(setEnrolledCode);
  }, []);

  useEffect(() => {
    loadProjects();
    
    // Load settings
    const storedShowLocation = localStorage.getItem('showLocationButtons');
    if (storedShowLocation !== null) {
      setShowLocation(storedShowLocation === 'true');
    }
    
    const storedEnableOvernight = localStorage.getItem('enableOvernightShifts');
    if (storedEnableOvernight !== null) {
      setEnableOvernight(storedEnableOvernight === 'true');
    }

    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setUserEmail(storedEmail);
    }

    // Load the current name so it can be shown and edited.
    db.getUserName().then(name => {
      if (name) {
        setUserName(name);
      }
    });

    // Sync Status interval
    const interval = setInterval(() => {
      setStatus(getSyncStatus());
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recount on open, on tab switch, and after any sync finishes. Deliberately
  // NOT on the 1s status interval: the count is a full table read, and nothing
  // can change it while this modal is the thing on screen.
  useEffect(() => {
    let cancelled = false;
    getPendingCount()
      .then((count) => { if (!cancelled) setPendingCount(count); })
      .catch((err) => console.error('Could not count pending entries:', err));
    return () => { cancelled = true; };
  }, [activeTab, status.lastSync]);

  // --- Projects Logic ---

  const loadProjects = async () => {
    try {
      const allProjects = await db.projects.toArray();
      setProjects(allProjects);
      if (allProjects.length > 0 && !selectedProject) {
        setSelectedProject(allProjects[0]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleAddProject = async () => {
    if (!newProjectName.trim()) return;
    
    try {
      // Check if project already exists
      const existingProjects = await db.projects
        .where('name')
        .equals(newProjectName.trim())
        .toArray();
      
      if (existingProjects.length === 0) {
        // Add new project to database
        const id = await db.projects.add({
          name: newProjectName.trim(),
          tasks: [],
          location: newProjectLocation.trim() || 'Site'
        });
        
        const newProject = await db.projects.get(id);
        if (newProject) {
          setProjects([...projects, newProject]);
          setSelectedProject(newProject);
        }
        setNewProjectName('');
      } else {
        alert('A project with this name already exists.');
      }
    } catch (error) {
      console.error('Error adding project:', error);
    }
  };

  const handleAddTask = async () => {
    if (!selectedProject || !newTaskName.trim()) return;
    
    try {
      // Check if task already exists for this project
      if (selectedProject.tasks.includes(newTaskName.trim())) {
        alert('This task already exists for the selected project.');
        return;
      }
      
      // Add task to the project
      const updatedTasks = [...selectedProject.tasks, newTaskName.trim()];
      await db.projects.update(selectedProject.id!, {
        tasks: updatedTasks
      });
      
      // Update local state
      const updatedProject = { ...selectedProject, tasks: updatedTasks };
      setSelectedProject(updatedProject);
      setProjects(projects.map(p => 
        p.id === selectedProject.id ? updatedProject : p
      ));
      setNewTaskName('');
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleDeleteTask = async (taskToDelete: string) => {
    if (!selectedProject) return;
    
    try {
      const updatedTasks = selectedProject.tasks.filter(task => task !== taskToDelete);
      await db.projects.update(selectedProject.id!, {
        tasks: updatedTasks
      });
      
      // Update local state
      const updatedProject = { ...selectedProject, tasks: updatedTasks };
      setSelectedProject(updatedProject);
      setProjects(projects.map(p => 
        p.id === selectedProject.id ? updatedProject : p
      ));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject || !window.confirm(`Are you sure you want to delete the project "${selectedProject.name}" and all its tasks?`)) {
      return;
    }
    
    try {
      await db.projects.delete(selectedProject.id!);
      
      // Update local state
      const updatedProjects = projects.filter(p => p.id !== selectedProject.id);
      setProjects(updatedProjects);
      setSelectedProject(updatedProjects.length > 0 ? updatedProjects[0] : null);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleImportData = () => {
    try {
      setImportError('');
      if (!importData.trim()) {
        setImportError('Please enter data to import');
        return;
      }

      // Parse the JSON data
      const data = JSON.parse(importData);
      
      // Validate the structure - support both array format and object with projects property
      let projectsArray;
      if (Array.isArray(data)) {
        // Direct array format
        projectsArray = data;
      } else if (data.projects && Array.isArray(data.projects)) {
        // Object with projects array property (standard format)
        projectsArray = data.projects;
        
        // If there's a defaultLocation, we could store it in localStorage for future use
        if (data.defaultLocation) {
          localStorage.setItem('defaultLocation', data.defaultLocation);
        }
      } else {
        setImportError('Invalid data format: Expected an array of projects or an object with a projects array');
        return;
      }
      
      // Confirm import
      if (!window.confirm(`This will import ${projectsArray.length} projects. Continue?`)) {
        return;
      }
      
      // Process the import
      processImport(projectsArray);
    } catch (error) {
      console.error('Error parsing import data:', error);
      setImportError('Invalid JSON format. Please check your data.');
    }
  };

  const processImport = async (data: any[]) => {
    try {
      // Start a transaction for all database operations
      await db.transaction('rw', db.projects, async () => {
        for (const item of data) {
          if (!item.name || !Array.isArray(item.tasks)) {
            console.warn('Skipping invalid project:', item);
            continue;
          }
          
          // Check if project exists
          const existingProjects = await db.projects
            .where('name')
            .equals(item.name)
            .toArray();
          
          if (existingProjects.length > 0) {
            // Merge tasks with existing project
            const existingProject = existingProjects[0];
            const existingTasks = new Set(existingProject.tasks);
            
            // Add new tasks
            for (const task of item.tasks) {
              existingTasks.add(task);
            }
            
            // Update project with tasks and possibly location
            const updateData: Partial<Project> = {
              tasks: Array.from(existingTasks)
            };
            
            // Update location if provided in the import
            if (item.location) {
              updateData.location = item.location;
            }
            
            await db.projects.update(existingProject.id!, updateData);
          } else {
            // Create new project with location if provided
            await db.projects.add({
              name: item.name,
              tasks: item.tasks,
              location: item.location || localStorage.getItem('defaultLocation') || 'Site'
            });
          }
        }
      });
      
      // Reload projects after import
      await loadProjects();
      setImportData('');
      alert('Import completed successfully!');
    } catch (error) {
      console.error('Error importing data:', error);
      setImportError('Error importing data. Please try again.');
    }
  };

  const handleExportData = () => {
    try {
      const exportData = JSON.stringify(projects.map(p => ({
        name: p.name,
        tasks: p.tasks
      })), null, 2);
      
      // Create a blob and download link
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'daytrakr-projects.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };
  
  const handleSyncUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSyncUrl(e.target.value);
    localStorage.setItem('projectsSyncUrl', e.target.value);
  };
  
  const handleSyncUrlClick = () => {
    if (syncUrl.trim()) {
      window.open(syncUrl, '_blank');
    } else {
      setSyncResult('Please enter a sync URL first');
    }
  };
  
  const handleSyncProjects = async () => {
    if (!syncUrl.trim()) {
      setSyncResult('Please enter a sync URL');
      return;
    }
    
    try {
      setIsSyncing(true);
      setSyncResult('Syncing projects...');
      
      // Fetch the JSON file from the provided URL
      const response = await fetch(syncUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Validate the structure - support both array format and object with projects property
      let projectsArray;
      if (Array.isArray(data)) {
        // Direct array format
        projectsArray = data;
      } else if (data.projects && Array.isArray(data.projects)) {
        // Object with projects array property (standard format)
        projectsArray = data.projects;
        
        // If there's a defaultLocation, we could store it in localStorage for future use
        if (data.defaultLocation) {
          localStorage.setItem('defaultLocation', data.defaultLocation);
        }
      } else {
        throw new Error('Invalid data format: Expected an array of projects or an object with a projects array');
      }
      
      // Process the import
      await processImport(projectsArray);
      
      setSyncResult('Projects synchronized successfully!');
    } catch (error) {
      console.error('Error syncing projects:', error);
      setSyncResult(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`); 
    } finally {
      setIsSyncing(false);
    }
  };

  // --- Preferences Logic ---
  const handleToggleLocation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setShowLocation(newValue);
    localStorage.setItem('showLocationButtons', String(newValue));
  };
  
  const handleToggleOvernight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setEnableOvernight(newValue);
    localStorage.setItem('enableOvernightShifts', String(newValue));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUserEmail(newValue);
    localStorage.setItem('userEmail', newValue);
  };

  // --- Device Enrollment Logic ---
  const handleChangeCode = async () => {
    setChangeCodeError(null);

    try {
      const outstanding = await db.getUnsyncedEntries();

      // Re-enrollment only. On a device that has never enrolled there is no
      // "previous employee" to file anything under, and completeEnrollment's
      // own preEnrollment reconciliation is the correct path for that case —
      // flushing here without a code would also be rejected outright once
      // REQUIRE_DEVICE_ENROLLMENT is switched on, making first-time setup
      // impossible.
      if (enrolledCode && outstanding.length > 0) {
        // These entries belong to the employee this device is enrolled as
        // RIGHT NOW. They have to go out under the CURRENT code, before
        // anything changes. Letting the new enrollment's handshake carry them
        // instead files them by NAME (that is what preEnrollment means to the
        // dashboard) — which on a re-enrollment is neither the old employee
        // nor necessarily the new one, and duplicates them if the name
        // resolves to a third profile.
        const proceed = window.confirm(
          `You have ${outstanding.length} ${outstanding.length === 1 ? 'entry' : 'entries'} ` +
          `that haven't been sent yet. They'll be sent now under this device's current code, ` +
          `so they stay with the person it is set up for. Continue?`
        );
        if (!proceed) return;

        const flush = await syncTimeEntries(outstanding);
        if (!flush.success) {
          setChangeCodeError(
            `Could not send this device's unsent ${outstanding.length === 1 ? 'entry' : 'entries'} ` +
            `under its current code: ${flush.message} The code was not changed — try again once that works.`
          );
          return;
        }
      }

      // The stored code is deliberately NOT cleared here. completeEnrollment
      // overwrites it once the NEW code has validated; clearing first left the
      // device unenrolled the moment the employee dismissed the modal.
      setShowEnrollment(true);
    } catch (error) {
      console.error('Error changing the device code:', error);
      setChangeCodeError('Could not check this device for unsynced entries. Try again.');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const result = await testConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
    <div className="modal-overlay">
      <div className="modal-content settings-modal">
        <div className="modal-header">
          <h2>Settings</h2>
          <div className="tabs">
            <button 
              className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveTab('projects')}
            >
              Projects
            </button>
            <button 
              className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              Preferences
            </button>
            <button 
              className={`tab-btn ${activeTab === 'sync' ? 'active' : ''}`}
              onClick={() => setActiveTab('sync')}
            >
              Cloud Sync
            </button>
            <button 
              className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
              onClick={() => setActiveTab('feedback')}
            >
              Feedback
            </button>
          </div>
        </div>
        
        <div className="modal-body">
          {activeTab === 'projects' && (
            <div className="projects-manager">
               <div className="projects-container">
                <div className="projects-list">
                  <h3>Projects</h3>
                  <div className="project-add">
                    <input
                      type="text"
                      placeholder="New project name"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Default location"
                      value={newProjectLocation}
                      onChange={(e) => setNewProjectLocation(e.target.value)}
                    />
                    <button onClick={handleAddProject}>Add</button>
                  </div>
                  <ul>
                    {projects.map(project => (
                      <li 
                        key={project.id} 
                        className={selectedProject?.id === project.id ? 'selected' : ''}
                        onClick={() => setSelectedProject(project)}
                      >
                        {project.name}
                        <span className="task-count">{project.tasks.length}</span>
                      </li>
                    ))}
                  </ul>
                  {selectedProject && (
                    <button 
                      className="delete-project-btn" 
                      onClick={handleDeleteProject}
                    >
                      Delete Project
                    </button>
                  )}
                </div>
                
                <div className="tasks-list">
                  <h3>Tasks for {selectedProject?.name || 'No Project Selected'}</h3>
                  {selectedProject && (
                    <>
                      <div className="task-add">
                        <input
                          type="text"
                          placeholder="New task name"
                          value={newTaskName}
                          onChange={(e) => setNewTaskName(e.target.value)}
                        />
                        <button onClick={handleAddTask}>Add</button>
                      </div>
                      <ul>
                        {selectedProject.tasks.map(task => (
                          <li key={task}>
                            {task}
                            <button 
                              className="delete-task-btn"
                              onClick={() => handleDeleteTask(task)}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                      {selectedProject.tasks.length === 0 && (
                        <p className="no-tasks">No tasks added yet</p>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <div className="import-export">
                <h3>Import/Export Projects</h3>
                <div className="import-section">
                  <textarea
                    placeholder="Paste JSON data to import projects and tasks"
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                  />
                  {importError && <p className="error">{importError}</p>}
                  <div className="import-buttons">
                    <button onClick={handleImportData}>Import</button>
                    <button onClick={handleExportData}>Export Current Projects</button>
                  </div>
                </div>
                
                <h3>Remote Sync</h3>
                <div className="sync-section">
                  <p className="helper-text">Enter a URL to a JSON file containing projects and tasks. This allows you to keep all devices in sync.</p>
                  <div className="sync-input">
                    <input 
                      type="url" 
                      value={syncUrl}
                      onChange={handleSyncUrlChange}
                      placeholder="Enter URL to JSON file"
                    />
                    <div className="sync-buttons">
                      <button 
                        onClick={handleSyncUrlClick}
                        className="view-json-btn"
                        disabled={!syncUrl.trim()}
                      >
                        View JSON
                      </button>
                      <button 
                        onClick={handleSyncProjects} 
                        disabled={isSyncing}
                        className={isSyncing ? 'syncing' : ''}
                      >
                        {isSyncing ? 'Syncing...' : 'Sync Projects'}
                      </button>
                    </div>
                  </div>
                  {syncResult && <p className={syncResult.includes('Error') ? 'error' : 'success'}>{syncResult}</p>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="preferences-section">
              <h3>App Preferences</h3>
              
              <div className="preference-item">
                <div className="preference-header">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={showLocation} 
                      onChange={handleToggleLocation} 
                    />
                    <span className="slider round"></span>
                  </label>
                  <span className="preference-title">Show Location Options</span>
                </div>
                <p className="preference-description">
                  Display the Site/Shop location toggle buttons in the clock-in and edit screens.
                </p>
              </div>

              <div className="preference-item">
                <div className="preference-header">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={enableOvernight} 
                      onChange={handleToggleOvernight} 
                    />
                    <span className="slider round"></span>
                  </label>
                  <span className="preference-title">Enable Overnight Shifts</span>
                </div>
                <p className="preference-description">
                  Allow selecting a different date for clocking out, for shifts that span across midnight.
                </p>
              </div>

              <h3>User Information</h3>
              <div className="form-group">
                <label htmlFor="userName">Your Name:</label>
                <input
                  type="text"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Your name"
                  className="email-input"
                />
                <p className="helper-text">
                  This name appears on your weekly timesheet.
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="userEmail">Email Address:</label>
                <input 
                  type="email" 
                  id="userEmail" 
                  value={userEmail} 
                  onChange={handleEmailChange} 
                  placeholder="your@email.com"
                  className="email-input"
                />
                <p className="helper-text">
                  Join our mailing list to get updates on the paid version and new features.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'sync' && (
            <div className="sync-settings-form">
              <div className="form-group">
                <small>
                  This device needs no sync setup. The dashboard address and its
                  credentials are held by the app itself, not stored on your phone.
                </small>
              </div>

              <div className="settings-section">
                <h3>Device setup</h3>
                {enrolledCode ? (
                  <p>This device is set up. Code ending <strong>{enrolledCode.slice(-3)}</strong>.</p>
                ) : (
                  <p>This device is not set up yet.</p>
                )}
                <button type="button" className="test-connection-btn" onClick={handleChangeCode}>
                  {enrolledCode ? 'Change code' : 'Enter code'}
                </button>
                {changeCodeError && (
                  <div className="test-result error">{changeCodeError}</div>
                )}
              </div>

              <div className="form-group">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="test-connection-btn"
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                
                {testResult && (
                  <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                    {testResult.message}
                  </div>
                )}
              </div>

              <div className="sync-status">
                <h3>Sync Status</h3>
                <div className="status-grid">
                  <div className="status-item">
                    <label>Last Sync:</label>
                    <span>{status.lastSync ? status.lastSync.toLocaleString() : 'Never'}</span>
                  </div>
                  
                  <div className="status-item">
                    <label>Pending Entries:</label>
                    <span>{pendingCount === null ? '…' : pendingCount}</span>
                  </div>
                  
                  <div className="status-item">
                    <label>Status:</label>
                    <span className={status.syncInProgress ? 'syncing' : 'idle'}>
                      {status.syncInProgress ? 'Syncing...' : 'Idle'}
                    </span>
                  </div>
                  
                  {status.lastError && (
                    <div className="status-item error">
                      <label>Last Error:</label>
                      <span>{status.lastError}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'feedback' && (
            <FeedbackForm />
          )}
        </div>
        
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Close</button>
          <button className="save-btn" onClick={async () => {
            // Save user name/email if they were changed. A blank name never
            // overwrites the stored name, since it feeds the timesheet and the
            // initial-setup gate.
            try {
              const existingName = await db.getUserName();
              const nameToSave = userName.trim() || existingName;
              if (nameToSave) {
                await db.setUserInfo(nameToSave, userEmail);
              }
            } catch (error) {
              console.error('Error saving user info:', error);
            }

            // Reload projects before closing to ensure fresh data if modified
            if (activeTab === 'projects') {
               loadProjects().then(() => {
                onSave();
              });
            } else {
              onSave();
            }
          }}>Done</button>
        </div>
      </div>
    </div>

    {showEnrollment && (
      <EnrollmentModal
        onComplete={async () => {
          setShowEnrollment(false);
          setEnrolledCode(await db.getEnrollmentCode());
        }}
        onDismiss={() => setShowEnrollment(false)}
      />
    )}
    </>
  );
};

export default Settings;
